import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackFormEvents } from '../../../../lib/analyticsEnhanced';
import { addSeconds, parseISO } from 'date-fns';
import { Duration, secondsToDuration, durationToSeconds } from '../../../../types/task';
import { BottomSheetModal } from '../../../../components/v3/BottomSheetModal';
import {
  IOSFormGroup,
  IOSFormItem,
  IOSFormLabel,
  IOSFormDateTime
} from '../ios/IOSFormGroup';
import { IOSDateQuickButtons } from '../ios/IOSDateQuickButtons';
import { IOSDurationInput } from '../ios/IOSDurationInput';

interface TaskTimeEditorProps {
  isOpen: boolean;
  onClose: () => void;
  startAt?: string;
  cost?: number;
  dueAt?: string;
  onSave: (data: { startAt?: string; cost?: number; dueAt?: string }) => void | Promise<void>;
}

// 计算结束时间
const calculateEndAt = (start: string, costInSeconds: number): string => {
  try {
    const startDate = parseISO(start);
    return addSeconds(startDate, costInSeconds).toISOString();
  } catch (error) {
    console.error("Error calculating end date:", error);
    return '';
  }
};

/**
 * 时间设置弹窗组件
 * 保持原有 TaskForm 中时间设置部分的完整功能
 */
export const TaskTimeEditor: React.FC<TaskTimeEditorProps> = ({
  isOpen,
  onClose,
  startAt,
  cost,
  dueAt,
  onSave
}) => {
  const { t } = useTranslation(['v3/tasks', 'common']);

  // 本地状态
  const [localStartAt, setLocalStartAt] = useState<string | undefined>(startAt);
  const [localCost, setLocalCost] = useState<number | undefined>(cost);
  const [localDueAt, setLocalDueAt] = useState<string | undefined>(dueAt);
  const [duration, setDuration] = useState<Duration>(() => {
    if (cost && !isNaN(cost)) {
      return secondsToDuration(cost);
    }
    return { days: 0, hours: 0, minutes: 0 };
  });

  // 计算的结束时间
  const endAt = localStartAt && localCost ? calculateEndAt(localStartAt, localCost) : undefined;

  // 当弹窗打开时重置本地状态
  useEffect(() => {
    if (isOpen) {
      setLocalStartAt(startAt);
      setLocalCost(cost);
      setLocalDueAt(dueAt);
      setDuration(cost && !isNaN(cost) ? secondsToDuration(cost) : { days: 0, hours: 0, minutes: 0 });
    }
  }, [isOpen, startAt, cost, dueAt]);

  // 处理时长变更
  const handleDurationChange = (newDuration: Duration) => {
    setDuration(newDuration);
    const newCost = durationToSeconds(newDuration);
    setLocalCost(newCost);
  };

  // 处理开始时间变更
  const handleStartAtChange = (newStartAt: string | undefined) => {
    setLocalStartAt(newStartAt);
  };

  // 处理结束时间变更
  const handleEndAtChange = (newEndAt: string | undefined) => {
    // 如果有开始时间和结束时间，重新计算时长
    if (localStartAt && newEndAt) {
      try {
        const startDate = parseISO(localStartAt);
        const endDate = parseISO(newEndAt);
        const newCost = Math.floor((endDate.getTime() - startDate.getTime()) / 1000);

        // 确保时长不为负数
        if (newCost >= 0) {
          setLocalCost(newCost);
          // 更新时长状态
          setDuration(secondsToDuration(newCost));
        } else {
          // 如果结束时间早于开始时间，使用开始时间作为结束时间
          // 这里不直接修改 newEndAt，而是保持用户输入，但不更新 cost
          console.warn("End time is before start time");
        }
      } catch (error) {
        console.error("Error calculating cost from endAt:", error);
        setLocalCost(undefined);
        setDuration({ days: 0, hours: 0, minutes: 0 });
      }
    } else {
      // 如果清除结束时间或开始时间不存在，清除cost
      setLocalCost(undefined);
      setDuration({ days: 0, hours: 0, minutes: 0 });
    }
  };

  // 处理截止时间变更
  const handleDueAtChange = (newDueAt: string | undefined) => {
    setLocalDueAt(newDueAt);
  };

  // 处理保存
  const handleSave = async () => {
    const changes = {
      startAt: localStartAt,
      cost: localCost,
      dueAt: localDueAt
    };

    // 记录编辑器保存事件
    trackFormEvents.editorSaved('time_editor', changes, {
      has_start_time: !!localStartAt,
      has_duration: !!localCost,
      has_due_time: !!localDueAt,
      duration_minutes: localCost ? Math.round(localCost / 60) : undefined
    });

    await onSave(changes);
    onClose();
  };

  return (
    <BottomSheetModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('v3/tasks:editors.timeSettings.title')}
      onConfirm={handleSave}
      confirmText={t('v3/tasks:editors.timeSettings.save')}
      cancelText={t('v3/tasks:editors.timeSettings.cancel')}
      useGrayBackground={true}
      usePortal={true}
    >
      <div className="p-4">
        {/* 开始时间 */}
        <IOSFormGroup>
          <IOSFormItem>
            <IOSFormLabel className="block mb-2">{t('v3/tasks:form.startTime')}</IOSFormLabel>
            <IOSFormDateTime
              value={localStartAt}
              onChange={handleStartAtChange}
            />
            <IOSDateQuickButtons
              currentValue={localStartAt}
              onChange={handleStartAtChange}
            />
          </IOSFormItem>
        </IOSFormGroup>

        {/* 时长输入 */}
        <IOSFormGroup>
          <IOSDurationInput
            duration={duration}
            onChange={handleDurationChange}
          />
        </IOSFormGroup>

        {/* 结束时间 */}
        <IOSFormGroup>
          <IOSFormItem>
            <IOSFormLabel className="block mb-2">{t('v3/tasks:form.endTime')}</IOSFormLabel>
            <IOSFormDateTime
              value={endAt}
              onChange={handleEndAtChange}
              disabled={!localStartAt} // 如果没有开始时间，禁用结束时间输入
            />
            <IOSDateQuickButtons
              currentValue={endAt}
              onChange={handleEndAtChange}
              isDisabled={!localStartAt} // 如果没有开始时间，禁用快速按钮
            />
          </IOSFormItem>
        </IOSFormGroup>

        {/* 截止时间 */}
        <IOSFormGroup>
          <IOSFormItem>
            <IOSFormLabel className="block mb-2">{t('v3/tasks:form.dueTime')}</IOSFormLabel>
            <IOSFormDateTime
              value={localDueAt}
              onChange={handleDueAtChange}
            />
            <IOSDateQuickButtons
              currentValue={localDueAt}
              onChange={handleDueAtChange}
            />
          </IOSFormItem>
        </IOSFormGroup>
      </div>
    </BottomSheetModal>
  );
};
